import {
  useState,
  useMemo,
  HTMLAttributes,
  useCallback,
  useRef,
  ReactNode,
  ReactElement,
  CSSProperties,
  useEffect,
  Children,
} from 'react';
import { List, useListRef } from 'react-window';
import {
  Autocomplete,
  TextField,
  CircularProgress,
  Box,
  BoxProps,
  Typography,
} from '@mui/material';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useDebounce } from 'src/hooks/use_debounce';
import { CursorPaginatedResponse } from 'src/types/pagination';

// ============================================================================
// Constants
// ============================================================================

/**
 * Height of each item in the virtualized list (in pixels).
 */
const ITEM_HEIGHT = 48;

/**
 * Maximum number of items visible before scrolling is required.
 * The list height will dynamically adjust based on item count, up to this maximum.
 */
const MAX_VISIBLE_ITEMS = 8;

/**
 * Number of items to fetch per page from the API.
 * Must be greater than MAX_VISIBLE_ITEMS to ensure proper infinite scrolling behavior.
 */
const PAGE_SIZE = 15;

/**
 * The number of pixels from the bottom of the list to trigger a fetch of the next page.
 */
const SCROLL_FETCH_THRESHOLD_PX = 20;

// ============================================================================
// Internal Virtualized List Component
// ============================================================================

interface VirtualizedListProps extends HTMLAttributes<HTMLElement> {
  ref?: React.Ref<HTMLDivElement>;
  getHasNextPage: () => boolean;
  getIsFetchingNextPage: () => boolean;
  fetchNextPage: () => void;
  sx?: BoxProps['sx'];
}

interface RowComponentProps {
  children: ReactElement[];
}

interface RowProps {
  ariaAttributes: {
    'aria-posinset': number;
    'aria-setsize': number;
    role: 'listitem';
  };
  index: number;
  style: CSSProperties;
}

const RowComponent = ({
  children,
  index,
  style,
}: RowProps & RowComponentProps): ReactElement => {
  return <div style={style}>{children[index]}</div>;
};

/**
 * Internal virtualized list component for VirtualizedAutocomplete.
 * Handles infinite scrolling, dynamic height, and scroll position preservation.
 */
function VirtualizedAutocompleteList(props: VirtualizedListProps) {
  const {
    ref,
    children,
    getHasNextPage,
    getIsFetchingNextPage,
    fetchNextPage,
    ...other
  } = props;

  const listRef = useListRef(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Convert children to array - store in ref to prevent re-renders
  const childrenArray = Children.toArray(children) as ReactElement[];
  const childrenRef = useRef(childrenArray);
  childrenRef.current = childrenArray;

  const itemCount = childrenArray.length;
  const dynamicHeight = Math.min(itemCount, MAX_VISIBLE_ITEMS) * ITEM_HEIGHT;

  // Setup infinite scroll handler
  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;

    // Find react-window's scrollable element
    const scrollableElement = wrapper.querySelector<HTMLElement>(
      '[style*="overflow"]',
    );
    if (!scrollableElement) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = scrollableElement;

      // Check if we should fetch more data
      const shouldFetch =
        getHasNextPage() &&
        !getIsFetchingNextPage() &&
        scrollTop + clientHeight >= scrollHeight - SCROLL_FETCH_THRESHOLD_PX;

      if (shouldFetch) {
        fetchNextPage();
      }
    };

    scrollableElement.addEventListener('scroll', handleScroll);
    return () => scrollableElement.removeEventListener('scroll', handleScroll);
  }, [getHasNextPage, getIsFetchingNextPage, fetchNextPage]);

  return (
    <Box
      ref={(node: HTMLDivElement | null) => {
        wrapperRef.current = node;

        // Forward ref to parent (handles both callback and object refs)
        if (ref) {
          if (typeof ref === 'function') {
            ref(node);
          } else {
            ref.current = node;
          }
        }
      }}
      {...other}
      sx={[
        {
          p: 1.25,
        },
        ...(Array.isArray(other.sx) ? other.sx : [other.sx]),
      ]}
    >
      <List
        listRef={listRef}
        rowHeight={ITEM_HEIGHT}
        rowCount={itemCount}
        rowComponent={RowComponent}
        rowProps={{ children: childrenRef.current }}
        overscanCount={5}
        style={{ height: dynamicHeight }}
      />
    </Box>
  );
}

// ============================================================================
// Public Autocomplete Component
// ============================================================================

interface VirtualizedAutocompleteProps<T> {
  // Data fetching
  queryKey: string[];
  fetchPage: (params: {
    searchText?: string;
    cursor?: string | null;
    first: number;
  }) => Promise<CursorPaginatedResponse<T>>;

  // Value management (controlled component: parent owns the selected item)
  value?: T | null; // Selected item
  onChange: (item: T | null) => void;
  getItemId: (item: T) => string;
  getItemLabel: (item: T | null) => string;

  // Rendering
  renderOption?: (item: T) => ReactNode;
  renderNoOptions?: (searchText: string, isLoading: boolean) => string;

  // Form props
  label: string;
  error?: boolean;
  helperText?: string;
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
  onBlur?: () => void;
}

export function VirtualizedAutocomplete<T>({
  queryKey,
  fetchPage,
  value,
  onChange,
  getItemId,
  getItemLabel,
  renderOption,
  renderNoOptions,
  label,
  error = false,
  helperText,
  required = false,
  disabled = false,
  placeholder,
  onBlur,
}: VirtualizedAutocompleteProps<T>) {
  const [inputValue, setInputValue] = useState('');
  const [searchText, setSearchText] = useState('');

  // Debounce the search text update
  const debouncedSetSearchText = useDebounce(setSearchText, 300);

  const { data, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage } =
    useInfiniteQuery<CursorPaginatedResponse<T>>({
      queryKey: [...queryKey, searchText],
      // pageParam is the cursor for the current page (null for first page)
      // TanStack Query automatically passes this from getNextPageParam
      queryFn: async ({ pageParam }) => {
        const cursor = pageParam as string | null | undefined;
        return fetchPage({
          searchText: searchText || undefined,
          cursor: cursor, // null for first page, cursor string for subsequent pages
          first: PAGE_SIZE,
        });
      },
      getNextPageParam: (lastPage) => lastPage.nextCursor,
      initialPageParam: null, // Start with null cursor (first page)
      enabled: !disabled,
    });

  // Flatten all pages into a single array
  const items = useMemo(() => {
    if (!data?.pages) return [];
    return data.pages.flatMap((page) =>
      (page.items || []).filter((item): item is T => item !== null),
    );
  }, [data?.pages]);

  // Store the latest values in refs so we can access them in callbacks without causing re-renders
  const hasNextPageRef = useRef(hasNextPage);
  const isFetchingNextPageRef = useRef(isFetchingNextPage);
  const fetchNextPageRef = useRef(fetchNextPage);

  // Update refs on every render
  hasNextPageRef.current = hasNextPage;
  isFetchingNextPageRef.current = isFetchingNextPage;
  fetchNextPageRef.current = fetchNextPage;

  // Create stable getters that always return the latest values
  const getHasNextPage = useCallback(() => hasNextPageRef.current, []);
  const getIsFetchingNextPage = useCallback(
    () => isFetchingNextPageRef.current,
    [],
  );
  const getAndCallFetchNextPage = useCallback(() => {
    fetchNextPageRef.current();
  }, []);

  // Create a stable ListboxComponent that uses getter functions.
  // Using useMemo here ensures the ListboxComponent reference remains
  // stable across renders so react-window doesn't reset scroll position
  // when new pages are loaded.
  const ListboxComponent = useMemo(
    () =>
      function ListboxWrapper(
        props: HTMLAttributes<HTMLElement> & {
          ref?: React.Ref<HTMLDivElement>;
        },
      ) {
        return (
          <VirtualizedAutocompleteList
            {...props}
            getHasNextPage={getHasNextPage}
            getIsFetchingNextPage={getIsFetchingNextPage}
            fetchNextPage={getAndCallFetchNextPage}
          />
        );
      },
    [getHasNextPage, getIsFetchingNextPage, getAndCallFetchNextPage],
  );

  // Custom filter to handle server-side search
  // We don't filter on the client - all filtering happens server-side via searchText
  const customFilterOptions = useCallback((options: T[]) => options, []);

  const defaultRenderOption = (item: T) => (
    <Typography variant='body2'>{getItemLabel(item)}</Typography>
  );

  const defaultRenderNoOptions = (searchText: string, isLoading: boolean) => {
    if (isLoading) return 'Loading...';
    if (searchText.trim()) return 'No results found';
    return `Type to search ${label.toLowerCase()}`;
  };

  return (
    <Autocomplete
      value={value}
      onChange={(_event, newValue) => {
        onChange(newValue);
      }}
      inputValue={inputValue}
      onInputChange={(_event, newInputValue, reason) => {
        setInputValue(newInputValue);

        if (reason === 'selectOption') {
          // Clear search when selecting an option that matches the
          // currently selected item to show all options in the menu.
          if (
            newInputValue.toLowerCase() ===
            getItemLabel(value || null).toLowerCase()
          ) {
            debouncedSetSearchText('');
          }
        }
        // User is typing/editing the input
        else if (reason === 'input') {
          // If there is a selected item and the user changes the text so it no longer
          // matches the selected item's label, clear the selection. This prevents the
          // Autocomplete from restoring the old label text after backspacing.
          const selectedLabel = getItemLabel(value || null);
          const normalizedInput = newInputValue.toLowerCase();
          const normalizedSelected = selectedLabel.toLowerCase();

          if (value && normalizedInput !== normalizedSelected) {
            onChange(null);
          }

          // Update search text based on the current input
          if (normalizedInput === normalizedSelected) {
            // Clear search when the typed input matches the selected item label
            debouncedSetSearchText('');
          } else {
            debouncedSetSearchText(newInputValue);
          }
        }
        // Blur should not change selection, but should keep search in sync
        else if (reason === 'blur') {
          const selectedLabel = getItemLabel(value || null);
          if (newInputValue.toLowerCase() === selectedLabel.toLowerCase()) {
            debouncedSetSearchText('');
          } else {
            debouncedSetSearchText(newInputValue);
          }
        }
        // Clear/reset input
        else if (['reset', 'clear'].includes(reason) && !newInputValue) {
          // Clear search when input is cleared or reset to show all options in the menu.
          debouncedSetSearchText('');
        }
      }}
      options={items}
      getOptionLabel={getItemLabel}
      isOptionEqualToValue={(option, value) =>
        !!value && getItemId(option) === getItemId(value)
      }
      loading={isLoading}
      disabled={disabled}
      filterOptions={customFilterOptions}
      slots={{
        listbox: ListboxComponent,
      }}
      onBlur={onBlur}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          required={required}
          error={error}
          helperText={helperText}
          onBlur={onBlur}
        />
      )}
      renderOption={(props, option) => (
        <li {...props} key={getItemId(option)}>
          <Box
            sx={{
              px: 1.25,
              py: 0.75,
              '&:hover': { backgroundColor: 'action.hover' },
            }}
          >
            {renderOption ? renderOption(option) : defaultRenderOption(option)}
          </Box>
        </li>
      )}
      noOptionsText={
        renderNoOptions
          ? renderNoOptions(searchText, isLoading)
          : defaultRenderNoOptions(searchText, isLoading)
      }
      loadingText={
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <CircularProgress size={16} />
          <Typography variant='body2'>
            {isFetchingNextPage ? `Loading next page...` : 'Loading...'}
          </Typography>
        </Box>
      }
    />
  );
}
